import { ReportAnalytics } from '@/components/Reports/blocks/ReportAnalytics';
import { RouteWrapper } from '@/routes/route-wrapper';
import React, { lazy, startTransition } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
// import { APP_SELECTION_SESSION_STORAGE_KEY } from '@/constants/constants';

/**
 * ============================================================================
 *
 *  Application Routes
 *
 * ============================================================================
 * 
 * We have a routes property containing an array of Route objects with all the
 * properties necessary to create a route + any other property that you wish
 * to pass to the component being rendered in that Route.
 * 
 * Each route object contains a category that's used to match Intent Names with
 * our Views/Categories. The route category organizes the views/pages of our
 * app by `UserType`.
 *
 * ----------------------------------------------------------------------------
 * 
 * Receiving Params from ChatBot
 * 
 * ----------------------------------------------------------------------------
 * 
 * If a certain path/route uses URL parameters, we need to create a 
 * `matchRouteWithSlots({...expected_params_from_chatBot})` method to do the 
 * mapping of the ChatBot's parameters with our Route's Parameter.
 * ```
 * {
 *    // Rest of the route properties (category, path, component...)
 *    matchRouteWithSlots: function({activityId='1F8783C4D2ED11E89543168CA1AFEB4A'}){
 *      return this.path.replace(':activityId', activityId);
 *    },
 * }
 * ```
 * 
 * In this example we're expecting the answer from the ChatBot to contain an
 * `activityId` property, if for any reason this property is coming back as 
 * `chatBot_activity_id` then we would have to change our code to be:
 * ```
 * {
 *    //Rest of the route properties (category, path, component...)
 *    matchRouteWithSlots: function({chatBot_activity_id='1F8783C4D2ED11E89543168CA1AFEB4A'}){
 *      return getCleanPath(this.path).replace(':activityId', chatBot_activity_id);
 *   },
 * }
 * ```
 * 
 * ----------------------------------------------------------------------------
 * IMPORTANT
 * ----------------------------------------------------------------------------
 *
 * Include the `:` when replacing a parameter with the desired value:
 * ```
 * getCleanPath(this.path).replace(':activityId', chatBot_activity_id);
 * //-----------------------------> ^
 * ```
 */

/**
 * Contains all the possible routes for the application
 * 
 * @note: Moved each route store to separate file for import() abstraction
 */

export const RoutesBuilder = ({
  routes,
  DEFAULT,
  ...props
}) => {
  const navigate = useNavigate();
  const handleSuspensefulNavigation = (path) => startTransition(() => navigate(path));
  const UnitsPage = lazy(() => import('@/features/manage_curriculum/pages/UnitsPage'));
  // TODO: Uncomment when IDserv will redirect to AEC
  // const AppSelectionPage = lazy(
  //   () => import("@/features/app_selection/AppSelectionPage")
  // );

  // useEffect(() => {
  //   if (!sessionStorage.getItem(APP_SELECTION_SESSION_STORAGE_KEY)) {
  //     navigate("/app-selection");
  //   }
  // }, []);
  
  return (
<Routes>
  {routes.map(({ 
    path,
    title,
    component,
    ...rest 
  }) => {
    const LazyComponent = lazy(() => component().then(module => ({default: module.default || module })));
    return (
    <Route
      key={path}
      path={path}
      element={
        <RouteWrapper
          component={LazyComponent}
          title={title}
          navigate={handleSuspensefulNavigation}
          {...rest}
          {...props}
        />
      }
    />
    )}
  )}
  <Route
    key="/district/curriculum-units"
    path="/district/curriculum-units/*"
    element={
      <RouteWrapper
        component={UnitsPage}
        title="Curriculum Units"
        navigate={handleSuspensefulNavigation}
        {...props}
      />
    } 
  />
    {/* <Route
    key="/app-selection"
    path="/app-selection/*"
    element={
      sessionStorage.getItem(APP_SELECTION_SESSION_STORAGE_KEY) ? (
        <Navigate to={DEFAULT} replace />
      ) : (
        <RouteWrapper
          component={AppSelectionPage}
          title="App Selection"
          navigate={handleSuspensefulNavigation}
          {...props}
        />
      )
    } 
  /> */}
  <Route
    path="*"
    element={<Navigate to={DEFAULT} replace />}
  />
</Routes>
)};


export const RouteComponentRenderer = ({
  location,
  path,
  title,
  component: Component,
  ...passthroughProps
}) => {
  const loggedPath = location?.pathname || path;
  return (
    <Route
      path={path}
      render={(props) => <ReportAnalytics path={loggedPath} title={title}>
        <Component {...props} {...passthroughProps}/>
      </ReportAnalytics>}
    />
  );
}